/**
 * Traffic Vector Tile Manager
 * 
 * Handles TomTom vector flow tiles integration with Mapbox
 * This provides more efficient traffic data rendering than the point-based approach
 */
export default class TrafficVectorTileManager {
  /**
   * Create a new Traffic Vector Tile Manager
   * @param {string} apiKey - TomTom API key
   */
  constructor(apiKey) {
    this.apiKey = apiKey;
    // Base URL for TomTom Vector Flow Tiles
    this.baseUrl = 'https://api.tomtom.com/traffic/map/4/tile/flow';
    
    // Default type is 'relative' - shows congestion relative to free-flow
    // Must be one of: 'relative', 'relative-delay', or 'absolute'
    this.flowType = 'relative';
    
    // Source and layer IDs
    this.sourceId = 'tomtom-traffic-vector-source';
    this.layerId = 'tomtom-traffic-vector-layer';
  }
  
  /**
   * Get Mapbox vector source configuration for TomTom vector tiles
   * @returns {Object} Mapbox source configuration
   */
  getVectorSource() {
    // Simplify to core essential tags only to avoid URL formatting issues
    // Traffic level is the most critical tag for coloring
    const tags = [
      'traffic_level',
      'road_type'
    ];
    
    // Create properly encoded tags parameter
    const encodedTags = encodeURIComponent('[' + tags.join(',') + ']');
    
    // Construct a simpler, more reliable URL
    const tileUrl = `${this.baseUrl}/${this.flowType}/{z}/{x}/{y}.pbf?key=${this.apiKey}&margin=0.1&tags=${encodedTags}`;
    
    console.log('[TrafficVectorTile] Tile URL template:', tileUrl);
    
    return {
      type: 'vector',
      tiles: [tileUrl],
      minzoom: 5,
      maxzoom: 20,
      attribution: 'Traffic data © TomTom'
    };
  }
  
  /**
   * Get Mapbox style configuration for traffic vector layer
   * @returns {Object} Mapbox layer style properties
   */
  getVectorLayerStyle() {
    return {
      'id': this.layerId,
      'source': this.sourceId,
      'source-layer': 'Traffic flow', // The source layer in TomTom vector tiles
      'type': 'line',
      'minzoom': 5,
      'layout': {
        'line-join': 'round',
        'line-cap': 'round',
        'visibility': 'visible'
      },
      'paint': {
        // Width based on zoom level and road type for better visibility
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          5, [
            'match',
            ['get', 'road_type'],
            'Motorway', 1.5,
            'International road', 1.2,
            'Major road', 1,
            0.8 // Default for other road types
          ],
          12, [
            'match',
            ['get', 'road_type'],
            'Motorway', 4,
            'International road', 3.5,
            'Major road', 3,
            'Secondary road', 2.5,
            'Connecting road', 2,
            1.5 // Default for other road types
          ],
          16, [
            'match',
            ['get', 'road_type'],
            'Motorway', 6,
            'International road', 5,
            'Major road', 4.5,
            'Secondary road', 4,
            'Connecting road', 3.5,
            'Major local road', 3,
            'Local road', 2.5,
            2 // Default for other road types
          ]
        ],
        // Color handling based on TomTom's numeric traffic_level property
        'line-color': [
          'case',
          // Check if road is closed
          ['==', ['get', 'road_closure'], true],
          '#000000', // Black for closed roads
          
          // Apply color scale based on min/max value check to detect flow type
          [
            'case',
            // For 'relative' flow type (values 0.00-1.00)
            // If traffic_level is between 0 and 1, it's likely relative flow
            ['all',
              ['>=', ['get', 'traffic_level'], 0],
              ['<=', ['get', 'traffic_level'], 1]
            ],
            [
              'interpolate',
              ['linear'],
              ['get', 'traffic_level'],
              0, '#E41B17',      // Red for 0% of free flow speed (complete congestion)
              0.4, '#E41B17',    // Red up to 40% of free flow
              0.5, '#F87217',    // Orange for 50% of free flow
              0.7, '#F87217',    // Orange up to 70% of free flow
              0.8, '#F9E076',    // Yellow for 80% of free flow
              0.9, '#4CC417',    // Green for 90%+ of free flow
              1, '#4CC417'       // Green for 100% of free flow speed
            ],
            
            // For 'absolute' flow type (speed in kph), use simpler interpolation
            // Simplified to fix the syntax error with nested expressions
            [
              'interpolate',
              ['linear'],
              ['get', 'traffic_level'],
              0, '#E41B17',    // Red for stopped traffic
              20, '#E41B17',   // Red up to 20 kph
              30, '#F87217',   // Orange for 30 kph
              50, '#F9E076',   // Yellow for 50 kph
              70, '#4CC417',   // Green for 70+ kph
              120, '#4CC417'   // Green for high speeds
            ]
          ]
        ],
        'line-opacity': 0.8
      },
      // More permissive filter - show any road with basic info
      // This ensures we render roads even if specific properties are missing
      'filter': ['any',
        ['has', 'traffic_level'],  // Roads with traffic level
        ['has', 'road_type'],      // Roads with type information
        ['has', 'freeFlowSpeed'],  // Roads with speed data
        ['==', ['get', 'road_closure'], true] // Closed roads
      ]
    };
  }
  
  /**
   * Sets the flow type ('relative', 'absolute', or 'relative-delay')
   * @param {string} type - The flow type to use
   * @param {Object} map - Optional map instance to update if layer already exists
   */
  setFlowType(type, map = null) {
    const previousType = this.flowType;
    
    if (['relative', 'absolute', 'relative-delay'].includes(type)) {
      this.flowType = type;
    } else {
      console.warn(`[TrafficVectorTile] Invalid flow type: ${type}, using 'relative'`);
      this.flowType = 'relative';
    }
    
    // Update existing source if map is provided and the type has changed
    if (map && previousType !== this.flowType && map.getSource(this.sourceId)) {
      try {
        // Need to completely rebuild the layer for source URL changes
        this.completelyRemoveVectorTrafficLayer(map);
        this.addVectorTrafficLayer(map);
        console.log(`[TrafficVectorTile] Updated flow type to ${this.flowType}`);
      } catch (error) {
        console.error('[TrafficVectorTile] Error updating flow type:', error);
      }
    }
    
    return this.flowType;
  }
  
  /**
   * Get a direct URL to a specific vector tile for testing
   * Useful for validating the TomTom API configuration
   * @param {number} z - Zoom level (5-22)
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {string} flowType - Optional flow type override
   * @returns {string} URL to the specific vector tile
   */
  getDebugTileUrl(z = 12, x = 1207, y = 1539, flowType = null) {
    // Simplify to core essential tags only - same as getVectorSource
    const tags = [
      'traffic_level',
      'road_type'
    ];
    
    // Create properly encoded tags parameter
    const encodedTags = encodeURIComponent('[' + tags.join(',') + ']');
    
    // Use provided flow type or default to the instance's flow type
    const type = flowType || this.flowType;
    
    // Generate URL for a specific tile to test with proper encoding
    const debugUrl = `${this.baseUrl}/${type}/${z}/${x}/${y}.pbf?key=${this.apiKey}&margin=0.1&tags=${encodedTags}`;
    
    console.log('[TrafficVectorTile] Debug tile URL:', debugUrl);
    return debugUrl;
  }
  
  /**
   * Toggle between absolute and relative flow types
   * @param {Object} map - The map instance to update
   * @returns {string} The new flow type
   */
  toggleFlowType(map) {
    if (!map) return this.flowType;
    
    // Toggle between the two most useful flow types
    const newType = this.flowType === 'absolute' ? 'relative' : 'absolute';
    console.log(`[TrafficVectorTile] Toggling flow type from ${this.flowType} to ${newType}`);
    
    // Update the flow type and rebuild the layer
    return this.setFlowType(newType, map);
  }
  
  /**
   * Add vector traffic layer to the map
   * @param {Object} map - Mapbox map instance
   * @param {boolean} enableDebug - Whether to enable debug features
   */
  addVectorTrafficLayer(map, enableDebug = true) {
    if (!map) {
      console.error('[TrafficVectorTile] Map is required');
      return;
    }
    
    try {
      console.log('[TrafficVectorTile] Adding vector traffic layer');
      
      // Add source if it doesn't exist
      if (!map.getSource(this.sourceId)) {
        console.log('[TrafficVectorTile] Adding vector source');
        const vectorSource = this.getVectorSource();
        map.addSource(this.sourceId, vectorSource);
        console.log('[TrafficVectorTile] Added vector source:', this.sourceId);
      }
      
      // Add layer if it doesn't exist
      if (!map.getLayer(this.layerId)) {
        console.log('[TrafficVectorTile] Adding vector layer');
        
        // Get layer style and ensure it has the correct ID
        const layerStyle = this.getVectorLayerStyle();
        console.log('[TrafficVectorTile] Layer style:', JSON.stringify({
          id: layerStyle.id,
          type: layerStyle.type,
          source: layerStyle.source,
          'source-layer': layerStyle['source-layer']
        }));
        
        map.addLayer(layerStyle);
        console.log('[TrafficVectorTile] Added vector layer:', this.layerId);
      } else {
        // If layer exists, update its visibility
        console.log('[TrafficVectorTile] Layer already exists, setting visibility');
        map.setLayoutProperty(this.layerId, 'visibility', 'visible');
      }
      
      console.log('[TrafficVectorTile] Successfully added vector traffic layer');
      
      // Enable debugging if requested
      if (enableDebug) {
        this.setupDebugFeatures(map);
      }
    } catch (error) {
      console.error('[TrafficVectorTile] Error adding vector traffic layer:', error);
      console.error(error.stack || error);
    }
  }
  
  /**
   * Set up debugging features to help diagnose vector tile issues
   * @param {Object} map - Mapbox map instance
   */
  setupDebugFeatures(map) {
    if (!map) return;
    
    try {
      console.log('[TrafficVectorTile] Setting up debug features');
      
      // Set up click handler to inspect features
      map.on('click', this.layerId, (e) => {
        // Get clicked features from our traffic layer
        const features = map.queryRenderedFeatures(e.point, {
          layers: [this.layerId]
        });
        
        if (features.length === 0) {
          console.log('[TrafficVectorTile] No traffic features found at click point');
          return;
        }
        
        // Log all properties of the first feature
        const props = features[0].properties;
        console.log('[TrafficVectorTile] Feature properties:', props);
        
        // Special focus on traffic_level value
        let trafficLevel = props.traffic_level;
        let trafficCategory = "Unknown";
        let trafficColor = "#CCCCCC";
        
        // Classify the traffic level
        if (trafficLevel !== undefined) {
          const numericLevel = parseFloat(trafficLevel);
          
          // Determine if this is relative or absolute flow based on value
          const isRelative = numericLevel >= 0 && numericLevel <= 1;
          console.log(`[TrafficVectorTile] Traffic level: ${numericLevel} (${isRelative ? 'relative' : 'absolute'} flow)`);
          
          if (isRelative) {
            // Relative flow (0-1)
            if (numericLevel <= 0.4) {
              trafficCategory = "Severe congestion";
              trafficColor = "#E41B17";
            } else if (numericLevel <= 0.7) {
              trafficCategory = "Heavy congestion";
              trafficColor = "#F87217";
            } else if (numericLevel <= 0.9) {
              trafficCategory = "Moderate congestion";
              trafficColor = "#F9E076";
            } else {
              trafficCategory = "Free flow";
              trafficColor = "#4CC417";
            }
          } else {
            // Absolute flow (speed in kph)
            const roadType = props.road_type || "Unknown";
            console.log(`[TrafficVectorTile] Road type: ${roadType}`);
            
            // Highways
            if (roadType === "Motorway" || roadType === "International road") {
              if (numericLevel < 40) {
                trafficCategory = "Severe congestion";
                trafficColor = "#E41B17";
              } else if (numericLevel < 60) {
                trafficCategory = "Heavy congestion";
                trafficColor = "#F87217";
              } else if (numericLevel < 80) {
                trafficCategory = "Moderate congestion";
                trafficColor = "#F9E076";
              } else {
                trafficCategory = "Free flow";
                trafficColor = "#4CC417";
              }
            }
            // Major roads
            else if (roadType === "Major road" || roadType === "Secondary road") {
              if (numericLevel < 30) {
                trafficCategory = "Severe congestion";
                trafficColor = "#E41B17";
              } else if (numericLevel < 45) {
                trafficCategory = "Heavy congestion";
                trafficColor = "#F87217";
              } else if (numericLevel < 60) {
                trafficCategory = "Moderate congestion";
                trafficColor = "#F9E076";
              } else {
                trafficCategory = "Free flow";
                trafficColor = "#4CC417";
              }
            }
            // Local roads
            else {
              if (numericLevel < 20) {
                trafficCategory = "Severe congestion";
                trafficColor = "#E41B17";
              } else if (numericLevel < 30) {
                trafficCategory = "Heavy congestion";
                trafficColor = "#F87217";
              } else if (numericLevel < 40) {
                trafficCategory = "Moderate congestion";
                trafficColor = "#F9E076";
              } else {
                trafficCategory = "Free flow";
                trafficColor = "#4CC417";
              }
            }
          }
        }
        
        // Create a more readable log of all properties
        const propList = Object.keys(props).map(key =>
          `${key}: ${props[key]}`
        ).join('\n');
        
        console.log('[TrafficVectorTile] Properties details:\n', propList);
        
        // Create a more useful popup with traffic information
        const html = `
          <div style="font-family: sans-serif; padding: 5px;">
            <h3 style="margin: 0 0 10px 0;">Traffic Data</h3>
            <div style="margin-bottom: 10px;">
              <strong>Traffic Level:</strong> <span style="color: ${trafficColor}; font-weight: bold;">${trafficLevel || 'N/A'}</span><br>
              <strong>Category:</strong> <span style="color: ${trafficColor}; font-weight: bold;">${trafficCategory}</span><br>
              <strong>Flow Type:</strong> ${this.flowType}<br>
              <strong>Road Type:</strong> ${props.road_type || 'Unknown'}<br>
            </div>
            <div style="max-height: 200px; overflow-y: auto; border-top: 1px solid #ccc; padding-top: 5px;">
              <pre style="margin: 0; font-size: 11px;">${propList}</pre>
            </div>
          </div>
        `;
        
        new mapboxgl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(html)
          .addTo(map);
      });
      
      // Add hover effect to make clickable areas obvious
      map.on('mouseenter', this.layerId, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      
      map.on('mouseleave', this.layerId, () => {
        map.getCanvas().style.cursor = '';
      });
      
      console.log('[TrafficVectorTile] Debug features set up - click on roads to inspect properties');
    } catch (error) {
      console.error('[TrafficVectorTile] Error setting up debug features:', error);
    }
  }
  
  /**
   * Remove (hide) vector traffic layer
   * @param {Object} map - Mapbox map instance
   */
  removeVectorTrafficLayer(map) {
    if (!map) {
      console.error('[TrafficVectorTile] Map is required');
      return;
    }
    
    try {
      if (map.getLayer(this.layerId)) {
        map.setLayoutProperty(this.layerId, 'visibility', 'none');
        console.log('[TrafficVectorTile] Hidden vector traffic layer');
      }
    } catch (error) {
      console.error('[TrafficVectorTile] Error hiding vector traffic layer:', error);
    }
  }
  
  /**
   * Completely remove vector traffic source and layer
   * @param {Object} map - Mapbox map instance
   */
  completelyRemoveVectorTrafficLayer(map) {
    if (!map) {
      console.error('[TrafficVectorTile] Map is required');
      return;
    }
    
    try {
      // Remove layer first
      if (map.getLayer(this.layerId)) {
        map.removeLayer(this.layerId);
      }
      
      // Then remove source
      if (map.getSource(this.sourceId)) {
        map.removeSource(this.sourceId);
      }
      
      console.log('[TrafficVectorTile] Completely removed vector traffic layer');
    } catch (error) {
      console.error('[TrafficVectorTile] Error completely removing vector traffic layer:', error);
    }
  }
}